import { atom } from 'nanostores';

import { api, type Invoice, type Plan, type Subscription } from '../lib/api';
import { isStaleSession, messageOf, onSessionCleared, refreshMe, sessionEpoch } from './auth';

import { track } from '../analytics/track';

export const $subscription = atom<Subscription | null>(null);
export const $currentPlan = atom<Plan | null>(null);
export const $pendingPlan = atom<Plan | null>(null);
export const $subHistory = atom<Subscription[]>([]);
export const $invoices = atom<Invoice[]>([]);
export const $billingStatus = atom('');
export const $graceEndsAt = atom<string | null>(null);
export const $provider = atom('manual');
export const $portalAvailable = atom(false);

export const $billingLoading = atom(false);
export const $billingError = atom<string | null>(null);
export const $billingNotice = atom<string | null>(null);
/** Plan code currently being switched to, so only that button spins. */
export const $switching = atom<string | null>(null);
export const $portalPending = atom(false);
/** Set while the scheduled change is being undone. */
export const $undoPending = atom(false);

/**
 * Drop everything that belongs to one customer. The pricing pages read this
 * store for the "current plan" and "scheduled change" wording, so leaving it
 * behind at sign-out advertises the previous account's subscription to whoever
 * is looking at the marketing site next.
 */
export function resetBilling(): void {
  $subscription.set(null);
  $currentPlan.set(null);
  $pendingPlan.set(null);
  $subHistory.set([]);
  $invoices.set([]);
  $billingStatus.set('');
  $graceEndsAt.set(null);
  $provider.set('manual');
  $portalAvailable.set(false);
  $billingLoading.set(false);
  $billingError.set(null);
  $billingNotice.set(null);
  $switching.set(null);
  $portalPending.set(false);
  $undoPending.set(false);
}

onSessionCleared(resetBilling);

export async function loadBilling(): Promise<void> {
  const captured = sessionEpoch();
  $billingLoading.set(true);
  $billingError.set(null);
  try {
    const res = await api.billing();
    if (isStaleSession(captured)) return;
    $subscription.set(res.subscription);
    $currentPlan.set(res.plan);
    $pendingPlan.set(res.pending_plan);
    $subHistory.set(res.history);
    $invoices.set(res.invoices ?? []);
    $billingStatus.set(res.billing_status ?? '');
    $graceEndsAt.set(res.grace_ends_at ?? null);
    $provider.set(res.provider);
    $portalAvailable.set(res.portal_available);
  } catch (err) {
    $billingError.set(messageOf(err));
  } finally {
    $billingLoading.set(false);
  }
}

/**
 * Switch plan. Three outcomes, because the server decides which applies:
 *
 *  - `checkout_url` → leave the SPA for the hosted payment page. Nothing has
 *    changed yet; the webhook applies the plan after payment.
 *  - `immediate`    → upgrade (or unbilled deployment): limits are live now.
 *  - `at_period_end`→ downgrade: current limits stay until renewal.
 *
 * Returns which of those happened so callers outside /app/billing know whether
 * the browser is already leaving for the payment page or whether they should
 * send the user somewhere that renders the resulting notice.
 */
export type SwitchOutcome = 'checkout' | 'applied' | 'error';

export async function switchPlan(planCode: string): Promise<SwitchOutcome> {
  $switching.set(planCode);
  $billingError.set(null);
  $billingNotice.set(null);
  try {
    track('upgrade_started', { to_plan: planCode });
    const res = await api.subscribe(planCode);

    if (res.checkout_url) {
      // Full navigation, not a router push: this leaves our origin.
      window.location.assign(res.checkout_url);
      return 'checkout';
    }

    const name = res.plan?.name ?? planCode;
    if (res.applied === 'at_period_end') {
      const when = res.effective_at ? new Date(res.effective_at).toLocaleDateString() : 'your renewal date';
      $billingNotice.set(
        `Scheduled: you move to ${name} on ${when}. Until then you keep your current limits — a smaller quota mid-cycle would cut you off retroactively.`,
      );
    } else {
      $billingNotice.set(`You are now on ${name}. New rate limits apply to your next API call.`);
    }

    // Only the paths that actually changed the plan here. A checkout_url means
    // the user has left for the payment provider and has not paid yet — that
    // conversion is confirmed by a webhook, not by this branch.
    track('upgrade_completed', { to_plan: planCode, applied: res.applied ?? 'immediate' });
    await Promise.all([loadBilling(), refreshMe()]);
    return 'applied';
  } catch (err) {
    $billingError.set(messageOf(err));
    return 'error';
  } finally {
    $switching.set(null);
  }
}

/** Undo whatever was booked for the renewal and stay on the current plan. */
export async function cancelScheduledChange(): Promise<void> {
  $undoPending.set(true);
  $billingError.set(null);
  $billingNotice.set(null);
  try {
    const res = await api.cancelScheduledChange();
    const when = res.renews_at ? new Date(res.renews_at).toLocaleDateString() : 'your renewal date';
    $billingNotice.set(`Scheduled change cancelled. Your plan renews as usual on ${when}.`);
    await Promise.all([loadBilling(), refreshMe()]);
  } catch (err) {
    $billingError.set(messageOf(err));
  } finally {
    $undoPending.set(false);
  }
}

/** Send the customer to the provider's hosted card / invoice / cancel page. */
export async function openPortal(): Promise<void> {
  $portalPending.set(true);
  $billingError.set(null);
  try {
    const { portal_url } = await api.billingPortal();
    window.location.assign(portal_url);
  } catch (err) {
    $billingError.set(messageOf(err));
  } finally {
    $portalPending.set(false);
  }
}
